// Core packages
import { formatDate } from "@angular/common";
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";

// Third party packages
import { MatDialog, MatPaginator, MatSort } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import {
  AlignmentType, Document, HeadingLevel, HorizontalPositionAlign, HorizontalPositionRelativeFrom, Media, Packer,
  Paragraph,
  TextRun, UnderlineType, VerticalPositionAlign, VerticalPositionRelativeFrom
} from "docx";
import Docxtemplater from 'docxtemplater';
import * as FileSaver from "file-saver";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils/index.js';
import { combineLatest, merge, Subscription } from "rxjs";
import {
  concatMap, debounceTime, distinctUntilChanged, finalize, startWith, take,
  tap
} from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { isAdditionalServiceActive } from "src/app/modules/companies/additional-services";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { FireSafetyDocsAddDocDialogComponent } from "src/app/modules/companies/components/add-fire-safety-doc-dialog/add-fire-safety-doc-dialog.component";
import { FireSafetyDocsEditDocDialogComponent } from "src/app/modules/companies/components/edit-fire-safety-doc-dialog/edit-fire-safety-doc-dialog.component";
import { FireSafetyDocsQrCodeDialogComponent } from "src/app/modules/companies/components/fire-safety-qr-code-dialog/fire-safety-qr-code-dialog.component";
import { PrintQrcodeDialogComponent } from 'src/app/modules/companies/components/print-qrcode-dialog/print-qrcode-dialog.component';
import { PrivacyStudioDocsAddDocDialogComponent } from 'src/app/modules/companies/components/add-privacy-studio-doc-dialog/add-privacy-studio-doc-dialog.component';
import { PrivacyStudioDocsEditDocDialogComponent } from 'src/app/modules/companies/components/edit-privacy-studio-doc-dialog/edit-privacy-studio-doc-dialog.component';
import { GeneratePrivacyDocsDialogComponent } from 'src/app/modules/companies/components/generate-privacy-docs-dialog/generate-privacy-docs-dialog.component';
import { PrivacyDocsLinkDialogComponent } from 'src/app/modules/companies/components/privacy-docs-link-dialog/privacy-docs-link-dialog.component';
import { PrivacyDocsQrCodeDialogComponent } from 'src/app/modules/companies/components/privacy-docs-qr-code-dialog/privacy-docs-qr-code-dialog.component';
import { PrivacyStudioDocsQrCodeDialogComponent } from 'src/app/modules/companies/components/privacy-studio-docs-qr-code-dialog/privacy-studio-docs-qr-code-dialog.component';
import { StudioPrivacyTemplatesDialogComponent } from 'src/app/modules/companies/components/studio-privacy-templates-dialog/studio-privacy-templates-dialog.component';
import { GenericDocsAddDocDialogComponent } from 'src/app/modules/generic-docs/components/add-doc-dialog/add-doc-dialog.component';
import { GenericDocsEditDocDialogComponent } from 'src/app/modules/generic-docs/components/edit-doc-dialog/edit-doc-dialog.component';
import { GenericDocsQrCodeDialogComponent } from 'src/app/modules/generic-docs/components/qr-code-dialog/qr-code-dialog.component';
import { GenericDocsService } from 'src/app/modules/generic-docs/generic-docs.service';
import { DocumentDownloadDialogComponent } from 'src/app/modules/properties/components/document-download-dialog/document-download-dialog.component';
import { SendEmailDialogComponent } from "src/app/modules/properties/components/send-email-dialog/send-email-dialog.component";
import { SuppliersService } from 'src/app/modules/suppliers/suppliers.service';
import { UsersService } from "src/app/modules/users/users.service";
import { AddCommunicationDialogComponent } from "src/app/shared/components/dialogs/add-communication-dialog/add-communication-dialog.component";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { VideoDialogComponent } from 'src/app/shared/components/dialogs/video-dialog/video-dialog.component';
import { ICompany } from "src/app/shared/models/company/company.interface";
import { Company } from "src/app/shared/models/company/company.model";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { FileManagerService } from "src/app/shared/services/file-manager.service";
import { FireSafetyDocsService } from 'src/app/shared/services/fire-safety-docs.service';
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { PrivacyDocsService, PROPERTY_PRIVACY_TEMPLATE, SUPPLIER_PRIVACY_TEMPLATE } from 'src/app/shared/services/privacy-docs.service';
import { InformativeNoteService } from 'src/app/modules/companies/informative-note.service';
import { PrivacyStudioDocsService } from 'src/app/shared/services/privacy-studio-docs.service';
import { ThemeService } from "src/app/shared/services/theme.service";
import { VideoViewService } from "src/app/shared/services/video-view.service";
import { FireSafetyDocsDataSource } from 'src/app/shared/utils/data-sources/fire-safety-docs-source';
import { GenericDocsDataSource } from 'src/app/shared/utils/data-sources/generic-docs-source';
import { PrivacyDocsDataSource } from 'src/app/shared/utils/data-sources/privacy-docs-source';
import { PrivacyStudioDocsDataSource } from 'src/app/shared/utils/data-sources/privacy-studio-docs-source';
import { VideoViewDataSource } from "src/app/shared/utils/data-sources/video-view-data-source";

@Component({
  selector: "app-view-company",
  templateUrl: "./view-company.page.html",
  styleUrls: ["./view-company.page.scss"],
})
export class ViewCompanyComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRequestedRole: string;
  loggedUserMaster = false;
  loggedUserAdminOrOperator = false;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  company: Company = new Company();
  companyId: string;
  detailCardIsLoading = true;
  companyData: ICompany;

  // Additional services: each section is shown only when its own contract flag is on
  safetyVideoSuppliersActive = false;
  safetyVideoResidentsActive = false;
  fireSafetyActive = false;

  // BEGIN - Add company form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  anagraphicFields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Add company form

  
  // BEGIN - Table safetyVideoSuppliers
  safetyVideoSuppliersDisplayedColumns: string[] = [
    "index", 
    "firstName",
    "lastName",
    "companyName",
    "createdAt",
  ];
  safetyVideoSuppliersDataSource: VideoViewDataSource;
  @ViewChild("safetyVideoSuppliersSort", { static: false }) safetyVideoSuppliersSort: MatSort;
  @ViewChild("safetyVideoSuppliersPaginator", { static: false }) safetyVideoSuppliersPaginator: MatPaginator;
  @ViewChild("safetyVideoSuppliersInput", { static: false }) safetyVideoSuppliersInput: ElementRef;
  safetyVideoSuppliersFirstNameFilter: FormControl = new FormControl("");
  safetyVideoSuppliersLastNameFilter: FormControl = new FormControl("");
  safetyVideoSuppliersCompanyNameFilter: FormControl = new FormControl("");
  safetyVideoSuppliersCreatedAtFilter: FormControl = new FormControl("");
  // END - Table safetyVideoSuppliers
  safetyVideoSuppliersQrcodeData: string;
  safetyVideoSuppliersCardIsLoading = false;

  // BEGIN - Table safetyVideoResidents
  safetyVideoResidentsDisplayedColumns: string[] = [
    "index", 
    "firstName",
    "lastName",
    "companyName",
    "createdAt",
  ];
  safetyVideoResidentsDataSource: VideoViewDataSource;
  @ViewChild("safetyVideoResidentsSort", { static: false }) safetyVideoResidentsSort: MatSort;
  @ViewChild("safetyVideoResidentsPaginator", { static: false }) safetyVideoResidentsPaginator: MatPaginator;
  @ViewChild("safetyVideoResidentsInput", { static: false }) safetyVideoResidentsInput: ElementRef;
  safetyVideoResidentsFirstNameFilter: FormControl = new FormControl("");
  safetyVideoResidentsLastNameFilter: FormControl = new FormControl("");
  safetyVideoResidentsCompanyNameFilter: FormControl = new FormControl("");
  safetyVideoResidentsCreatedAtFilter: FormControl = new FormControl("");
  // END - Table safetyVideoResidents
  safetyVideoResidentsQrcodeData: string;
  safetyVideoResidentsCardIsLoading = false;


  // BEGIN - Table generic docs
  genericDocsDisplayedColumns: string[] = ['index', 'name', 'description', 'createdAt', 'createdBy', 'actions'];
  genericDocsDataSource: GenericDocsDataSource;
  @ViewChild('genericDocsSort', { static: false }) genericDocsSort: MatSort;
  @ViewChild('genericDocsPaginator', { static: false }) genericDocsPaginator: MatPaginator;
  // @ViewChild('genericDocsInput', { static: false }) genericDocsInput: ElementRef;
  genericDocsNameFilter: FormControl = new FormControl('');
  genericDocsDescriptionFilter: FormControl = new FormControl('');
  genericDocsCreatedAtFilter: FormControl = new FormControl('');
  // END - Table generic docs
  genericDocsCardIsLoading = false;

  // BEGIN - Table fire safety docs - default
  fireSafetyDocsDefaultDataSource = [
    {
      _id: "68e62051087b3792d6b89aaa",
      name: this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.name"),
      description: this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.description"),
      createdAt: new Date(this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.createdAt")),
      files: [
        {
          _id: "6634d6c7c3c77915076f5c8c",
          originalName: "GSA-SAG-LP-ALL.pdf",
          filename: "GSA-SAG-LP-ALL.pdf",
          path: "uploads/firesafetydocs/GSA-SAG-LP-ALL.pdf",
          mimetype: "application/pdf",
          size: 2056192,
          createdBy: "5ef210e3e5da4742563f4c75",
          createdAt: "2024-05-17T12:21:25.740+00:00",
          updatedAt: "2024-05-17T12:21:25.740+00:00"
        }
      ]
    }
  ];
  fireSafetyDocsefaultDisplayedColumns: string[] = ['index', 'name', 'description', 'createdAt', 'actions'];
  // END - Table fire safety docs - default

  // BEGIN - Table fire safety docs
  fireSafetyDocsDisplayedColumns: string[] = ['index', 'name', 'description', 'createdAt', 'actions'];
  fireSafetyDocsDataSource: FireSafetyDocsDataSource;
  @ViewChild('fireSafetyDocsSort', { static: false }) fireSafetyDocsSort: MatSort;
  @ViewChild('fireSafetyDocsPaginator', { static: false }) fireSafetyDocsPaginator: MatPaginator;
  // @ViewChild('fireSafetyDocsInput', { static: false }) fireSafetyDocsInput: ElementRef;
  fireSafetyDocsNameFilter: FormControl = new FormControl('');
  fireSafetyDocsDescriptionFilter: FormControl = new FormControl('');
  fireSafetyDocsCreatedAtFilter: FormControl = new FormControl('');
  // END - Table fire safety docs
  fireSafetyDocsCardIsLoading = false;

  // BEGIN - Table privacy docs
  privacyDocsDisplayedColumns: string[] = ['index', 'type', 'description', 'createdAt', 'actions'];
  privacyDocsDataSource: PrivacyDocsDataSource;
  @ViewChild('privacyDocsSort', { static: false }) privacyDocsSort: MatSort;
  @ViewChild('privacyDocsPaginator', { static: false }) privacyDocsPaginator: MatPaginator;
  // END - Table privacy docs

  // BEGIN - Table "Nota informativa" docs (condo-owner privacy notice only)
  informativeNoteDocsDataSource: PrivacyDocsDataSource;
  @ViewChild('informativeNoteDocsSort', { static: false }) informativeNoteDocsSort: MatSort;
  @ViewChild('informativeNoteDocsPaginator', { static: false }) informativeNoteDocsPaginator: MatPaginator;
  // END - Table "Nota informativa" docs
  privacyDocsCardIsLoading = true;
  privacyDocsSendPecDisabled = false;

  // BEGIN - Table privacy studio docs
  privacyStudioDocsDisplayedColumns: string[] = ['index', 'checkbox', 'type', 'description', 'createdAt', 'actions'];
  privacyStudioDocsDataSource: PrivacyStudioDocsDataSource;
  @ViewChild('privacyStudioDocsSort', { static: false }) privacyStudioDocsSort: MatSort;
  @ViewChild('privacyStudioDocsPaginator', { static: false }) privacyStudioDocsPaginator: MatPaginator;
  privacyStudioDocsTypeFilter: FormControl = new FormControl('');
  privacyStudioDocsDescriptionFilter: FormControl = new FormControl('');
  privacyStudioDocsCreatedAtFilter: FormControl = new FormControl('');
  selectedPrivacyStudioDocs: any[] = [];
  // END - Table privacy studio docs

  suppliers = true;
  hiddenCardPrivacy = true;
  privacySagActive = false;
  privacyActive = false;
  // "Nota informativa": condo-owner privacy access key without Privacy SAG
  informativeNoteActive = false;
  informativeNoteDocIsLoading = false;
  availableProperties: PropertyInterface[] = [];

  // disclaimer
  disclaimer = false;
  disclaimerAt = '';
  disclaimerUser = '';

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private companiesService: CompaniesService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    public newMessageDialog: MatDialog,
    private cookieService: CookieService,
    private fileService: FileManagerService,
    private listsService: ListsService,
    private videoViewService: VideoViewService,
    private genericDocsService: GenericDocsService,
    private fireSafetyDocsService: FireSafetyDocsService,
    private usersService: UsersService,
    private privacyDocsService: PrivacyDocsService,
    private privacyStudioDocsService: PrivacyStudioDocsService,
    private suppliersService: SuppliersService,
    private informativeNoteService: InformativeNoteService,
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    this.companyId = this.cookieService.get("requestedCompany");

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );
    this.subscriptions.push(
      this.authService.loggedUserRequestedRole$.subscribe(
        (res) => (this.loggedUserRequestedRole = res)
      )
    );

    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return obj._id.toString() === this.loggedUserRequestedRole.toString();
    });

    // If requesting user is not master or collaborator, reject request and redirect to home
    if (
      this.currentRole.role.type === "master" ||
      this.currentRole.role.type === "collaborator"
    ) {
      this.loggedUserMaster = true;
    }

    if (
      this.currentRole.role.type === "admin" ||
      this.currentRole.role.type === "operator"
    ) {
      this.loggedUserAdminOrOperator = true;
    }

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.getCompany();

    // Build QR code URLs eagerly to avoid ExpressionChangedAfterItHasBeenCheckedError
    const host = this.configService.frontEndHost;
    this.safetyVideoSuppliersQrcodeData = `${host}/qrcode/${this.companyId}/safetyVideoSuppliers`;
    this.safetyVideoResidentsQrcodeData = `${host}/qrcode/${this.companyId}/safetyVideoResidents`;

    
    this.safetyVideoSuppliersDataSource = new VideoViewDataSource(
      this.videoViewService,
      this.helperService
    );

    this.safetyVideoResidentsDataSource = new VideoViewDataSource(
      this.videoViewService,
      this.helperService
    );

    this.genericDocsDataSource = new GenericDocsDataSource(
      this.genericDocsService, 
      this.helperService
    );

    this.fireSafetyDocsDataSource = new FireSafetyDocsDataSource(
      this.fireSafetyDocsService,
      this.helperService
    );

    this.privacyDocsDataSource = new PrivacyDocsDataSource(
      this.privacyDocsService,
      this.helperService
    );

    this.informativeNoteDocsDataSource = new PrivacyDocsDataSource(
      this.privacyDocsService,
      this.helperService
    );

    this.privacyStudioDocsDataSource = new PrivacyStudioDocsDataSource(
      this.privacyStudioDocsService,
      this.helperService
    );
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    
    this.initSafetyVideoSuppliersDataTable();
    this.safetyVideoSuppliersCardIsLoading = false;

    this.initSafetyVideoResidentsDataTable();
    this.safetyVideoResidentsCardIsLoading = false;

    this.initGenericDocsDataTable();
    this.genericDocsCardIsLoading = false;

    this.initFireSafetyDocsDataTable();
    this.fireSafetyDocsCardIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    this.fileService.reset();

    // Push current list settings to the listsService
    if (this.safetyVideoSuppliersPaginator) {
      // Push current list settings to the listsService
      this.listsService.setListSetting(
        "safetyVideoSuppliers",
        this.safetyVideoSuppliersPaginator.pageIndex.valueOf(),
        this.safetyVideoSuppliersPaginator.pageSize.valueOf(),
        {
          firstNameFilter: this.safetyVideoSuppliersFirstNameFilter.value,
          lastNameFilter: this.safetyVideoSuppliersLastNameFilter.value,
          companyNameFilter: this.safetyVideoSuppliersCompanyNameFilter.value,
          createdAtFilter: this.safetyVideoSuppliersCreatedAtFilter.value,
        }
      );
    }

    if (this.safetyVideoResidentsPaginator) {
      // Push current list settings to the listsService
      this.listsService.setListSetting(
        "safetyVideoResidents",
        this.safetyVideoResidentsPaginator.pageIndex.valueOf(),
        this.safetyVideoResidentsPaginator.pageSize.valueOf(),
        {
          firstNameFilter: this.safetyVideoResidentsFirstNameFilter.value,
          lastNameFilter: this.safetyVideoResidentsLastNameFilter.value,
          companyNameFilter: this.safetyVideoResidentsCompanyNameFilter.value,
          createdAtFilter: this.safetyVideoResidentsCreatedAtFilter.value,
        }
      );
    }
    if (this.genericDocsPaginator) {
      this.listsService.setListSetting(
        'genericDocs',
        this.genericDocsPaginator.pageIndex.valueOf(),
        this.genericDocsPaginator.pageSize.valueOf(),
        {
          name: this.genericDocsNameFilter.value,
          description: this.genericDocsDescriptionFilter.value,
          createdAt: this.genericDocsCreatedAtFilter.value,
        }
      );
  
      if (this.fireSafetyDocsPaginator) {
        // Push current list settings to the listsService
        this.listsService.setListSetting(
          'fireSafetyDocs',
          this.fireSafetyDocsPaginator.pageIndex.valueOf(),
          this.fireSafetyDocsPaginator.pageSize.valueOf(),
          {
            name: this.fireSafetyDocsNameFilter.value,
            description: this.fireSafetyDocsDescriptionFilter.value,
            createdAt: this.fireSafetyDocsCreatedAtFilter.value,
          }
        );
      }
    }

    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Get company data
   */
  getCompany(): void {
    this.subscriptions.push(
      this.companiesService.get(this.companyId).subscribe(
        (res) => {
          if (res.status) {
            // Set company data
            this.company.setData(res.data);
            this.companyData = res.data;

            const companySubscription = this.company.get('subscription');

            // Companies saved before the split inherit the legacy "additionalServices" flag
            this.safetyVideoSuppliersActive = isAdditionalServiceActive(companySubscription, "safetyVideoSuppliers");
            this.safetyVideoResidentsActive = isAdditionalServiceActive(companySubscription, "safetyVideoResidents");
            this.fireSafetyActive = isAdditionalServiceActive(companySubscription, "fireSafety");

            this.hiddenCardPrivacy = !(
              companySubscription &&
              (companySubscription.privacy || companySubscription.privacySag)
            );
            this.privacySagActive = !!(companySubscription && companySubscription.privacySag);
            this.privacyActive = !!(companySubscription && companySubscription.privacy);
            this.informativeNoteActive = !!(companySubscription && companySubscription.informativeNote);
            this.suppliers = !!(companySubscription && companySubscription.suppliers);

            this.getAvailableProperties();

            this.initPrivacyDocsDataTable();
            this.initInformativeNoteDocsDataTable();
            this.privacyDocsCardIsLoading = false;

            this.initPrivacyStudioDocsDataTable();

            // Disclaimer
            if (
              res.data.disclaimer &&
              res.data.disclaimer.check1 &&
              res.data.disclaimer.check2 &&
              res.data.disclaimer.check3 &&
              res.data.disclaimer.check4
            ) {
              this.disclaimer = true;
              this.disclaimerAt = res.data.disclaimer.dateCheck1;
              this.subscriptions.push(
                this.usersService.get(res.data.disclaimer.check1UpdatedBy).subscribe(
                  (res) => {
                    if (res.status) {
                      this.disclaimerUser = `${res.data.firstName} ${res.data.lastName}`;
                    }
                  }
                )
              );
            }

            // Init edit form
            setTimeout(() => {
              this.initForm();
            }, 500);

            // Make detail form card visible by disabling his card-spinner
            this.detailCardIsLoading = false;

            // If there are no subProperties, let's create an empty array
            if (!this.company.get("subProperties")) {
              this.company.set("subProperties", []);
            }
            return;
          }

          // Something bad happened
          // redirect user to home
          this.router.navigate([this.lang, "app", "companies", "list"]);
        },
        (err) => {
          // if we came here users with given userId do not exist
          this.helperService.handleError(err);
          this.router.navigate([this.lang, "errors", "404"]);
        }
      )
    );
  }

  /**
   * Get the list of this company's own properties, used to populate the
   * "generate privacy documents" dialog
   *
   * @since 1.0.0
   */
  getAvailableProperties(): void {
    this.subscriptions.push(
      this.companiesService
        .getSubPropertiesList(this.company.getId(), 0)
        .subscribe((res) => {
          if (res.status && res.data.items && Array.isArray(res.data.items)) {
            this.availableProperties = res.data.items;
          }
        })
    );
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = this.company.getData();

    const categoriesOptions: any[] = [];
    const rawCategories = this.translateService.instant("companies.categories");
    for (const trans of Object.keys(rawCategories)) {
      categoriesOptions.push({
        label: rawCategories[trans],
        value: trans,
      });
    }

    // Set fields
    this.anagraphicFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant("companies.fields.name"),
              placeholder: this.translateService.instant(
                "companies.fields.name"
              ),
              required: true,
              minLength: 2,
              maxLength: 100,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "select",
            key: "types",
            templateOptions: {
              label: this.translateService.instant("companies.fields.types"),
              placeholder: this.translateService.instant(
                "companies.fields.types"
              ),
              required: true,
              multiple: true,
              disabled: true,
              options: [
                {
                  value: "owner",
                  label: this.translateService.instant("companies.types.owner"),
                },
                {
                  value: "manager",
                  label: this.translateService.instant(
                    "companies.types.manager"
                  ),
                },
              ],
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "vatNumber",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.vatNumber"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.vatNumber"
              ),
              required: false,
              minLength: 0,
              maxLength: 20,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "taxCode",
            templateOptions: {
              label: this.translateService.instant("companies.fields.taxCode"),
              placeholder: this.translateService.instant(
                "companies.fields.taxCode"
              ),
              required: false,
              minLength: 0,
              maxLength: 20,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "email",
            templateOptions: {
              label: this.translateService.instant("companies.fields.email"),
              placeholder: this.translateService.instant(
                "companies.fields.email"
              ),
              required: true,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "pec",
            templateOptions: {
              label: this.translateService.instant("companies.fields.pec"),
              placeholder: this.translateService.instant(
                "companies.fields.pec"
              ),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "sdiCode",
            templateOptions: {
              label: this.translateService.instant("companies.fields.sdiCode"),
              placeholder: this.translateService.instant(
                "companies.fields.sdiCode"
              ),
              required: false,
              minLength: 6,
              maxLength: 7,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "address",
            key: "legalAddress",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalAddress"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalAddress"
              ),
              required: false,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "address",
            key: "operativeAddress",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.operativeAddress"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.operativeAddress"
              ),
              required: false,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "phone",
            templateOptions: {
              label: this.translateService.instant("companies.fields.phone"),
              placeholder: this.translateService.instant(
                "companies.fields.phone"
              ),
              required: true,
              minLength: 1,
              maxLength: 15,
              pattern: this.configService.phonePattern,
            },
          },
          {
            className: "col-12 col-md-8",
            type: "input",
            key: "webSiteUrl",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.webSiteUrl"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.webSiteUrl"
              ),
              required: false,
              minLength: 1,
              maxLength: 100,
              pattern: this.configService.webSitePattern,
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid"),
        this.translateService.instant("common.words.error")
      );
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("companies.dialog.saveMessage"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        // Activate submit button loading status
        this.formBtnOptions.active = true;

        // Remove subscription from model if company is not of "manager" type
        if (model && model.types && !model.types.includes("manager")) {
          if (model.subscription) {
            delete model.subscription;
          }
        }

        this.subscriptions.push(
          this.companiesService
            .update(this.company.getId(), model)
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                if (res.status) {
                  this.toastrService.success(res.message);
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                // this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      });
  }



   
  /**
   * Init safetyVideoSuppliers data table
   *
   * @since 1.0.0
   */
  initSafetyVideoSuppliersDataTable(): void {
    // Load first set of data
    this.loadSafetyVideoSuppliersPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.safetyVideoSuppliersSort.sortChange.subscribe(
        () => (this.safetyVideoSuppliersPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.safetyVideoSuppliersSort.sortChange, this.safetyVideoSuppliersPaginator.page)
        .pipe(tap(() => this.loadSafetyVideoSuppliersPage()))
        .subscribe()
    );

    // // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.safetyVideoSuppliersInput.nativeElement, "keyup")
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.safetyVideoSuppliersPaginator.pageIndex = 0; // Reset page
    //         this.loadSafetyVideoSuppliersPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    combineLatest([
      this.safetyVideoSuppliersFirstNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoSuppliersLastNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoSuppliersCompanyNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoSuppliersCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
    ])
      .pipe(
        debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
      )
      .subscribe((res) => {
        // Load page only if at least one value is not null
        if (!res.every((value) => value === null)) {
          this.loadSafetyVideoSuppliersPage();
        }
      });
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadSafetyVideoSuppliersPage(): void {
    const start = this.safetyVideoSuppliersPaginator.pageIndex * this.safetyVideoSuppliersPaginator.pageSize;
    const length = this.safetyVideoSuppliersPaginator.pageSize;
    const search = {
      firstName: this.safetyVideoSuppliersFirstNameFilter.value,
      lastName: this.safetyVideoSuppliersLastNameFilter.value,
      companyName: this.safetyVideoSuppliersCompanyNameFilter.value,
      createdAt: this.safetyVideoSuppliersCreatedAtFilter.value,
    };
    this.safetyVideoSuppliersDataSource.loadSuppliersVideoItems(
      this.companyId,
      start,
      length,
      this.safetyVideoSuppliersSort.active,
      this.safetyVideoSuppliersSort.direction,
      search
    );
  }

  
  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFiltersSafetyVideoSuppliers(): void {
    // Reset lists service data
    this.listsService.setListSetting("safetyVideoSuppliers", 0, 10, {});

    // Reset tables' filters
    // this.safetyVideoSuppliersInput.nativeElement.value = "";
    this.safetyVideoSuppliersFirstNameFilter.setValue(null);
    this.safetyVideoSuppliersLastNameFilter.setValue(null);
    this.safetyVideoSuppliersCompanyNameFilter.setValue(null);
    this.safetyVideoSuppliersCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.safetyVideoSuppliersSort.direction = "asc";
    this.safetyVideoSuppliersSort.active = "createdAt";
    this.safetyVideoSuppliersSort._stateChanges.next();

    // Reset safetyVideoSuppliersPaginator
    this.safetyVideoSuppliersPaginator.pageIndex = 0;
    this.safetyVideoSuppliersPaginator.pageSize = 10;

    // Reload table's data
    this.loadSafetyVideoSuppliersPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilterSafetyVideoSuppliers(filter: FormControl): void {
    filter.reset();
    this.loadSafetyVideoSuppliersPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInputSafetyVideoSuppliers(): void {
    this.safetyVideoSuppliersInput.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    this.safetyVideoSuppliersInput.nativeElement.dispatchEvent(event);
  }
  
  /**
   * Handle click open QRcode safetyVideoSuppliers dialogbox
   *
   * @since 1.0.0
   */
  onSafetyVideoSuppliersPrintQRcode(): void {
    this.dialog.open(PrintQrcodeDialogComponent, {
      data: {
        qrCode: this.safetyVideoSuppliersQrcodeData,
        name: this.company.get("name"),
        city: this.company.get("legalAddress.city"),
        address: `${this.company.get("legalAddress.route")} ${this.company.get("legalAddress.streetNumber")}`
      },
      width: "700px",
    });
  }

  /**
   * Handle generate QRcode docx
   *
   * @since 1.0.0
   */
  onSafetyVideoSuppliersGenerateQrCodeDoc(): void {
    const qrImage = document
      .getElementById("safetyVideoSuppliersQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Create document data
    const doc = new Document();
    const image = Media.addImage(doc, qrImage, 150, 150, {
      floating: {
        horizontalPosition: {
          relative: HorizontalPositionRelativeFrom.PAGE,
          align: HorizontalPositionAlign.CENTER,
        },
        verticalPosition: {
          relative: VerticalPositionRelativeFrom.PARAGRAPH,
          align: VerticalPositionAlign.CENTER,
        },
      },
    });

    doc.addSection({
      properties: {},
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 400 },
          children: [
            new TextRun({
              text: `AMMINISTRATORE ${this.company.get("name")}`,
              bold: true,
              allCaps: true,
              color: "auto",
              size: 24 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          children: [
            new TextRun({
              text: `ATTENZIONE`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 28 * 2,
              underline: {
                color: "FF0000",
                type: UnderlineType.SINGLE,
              },
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: `AVVISO A TUTTI I FORNITORI`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 28 * 2,
              underline: {
                color: "FF0000",
                type: UnderlineType.SINGLE,
              },
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: `PRIMA DI ACCEDERE ALLE PARTI COMUNI DEL CONDOMINIO E' NECESSARIO VISUALIZZARE L'INFORMATIVA SULLA SICUREZZA E I DIVIETI`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 22 * 2,
            }),
          ],
        }),
        new Paragraph(image),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 2500 },
          children: [
            new TextRun({
              text: `VIDEO INFORMATIVO AMMINISTRATORE ${this.company.get("name")}`,
              font: "Times New Roman",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 12 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_2,
          children: [
            new TextRun({
              text: `${this.company.get("legalAddress.route")}, ${this.company.get(
                "legalAddress.streetNumber"
              )}`,
              font: "Times New Roman",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 12 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 2000, after: 200 },
          children: [
            new TextRun({
              text: `SI PREGA DI INQUADRARE IL QR-CODE DIRETTAMENTE CON LA FOTOCAMERA DEL VOSTRO DISPOSITIVO (SE ABILITATO) O ATTRAVERSO UN'APPLICAZIONE DEDICATA.`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: `DOPO L'INQUADRATURA IL VIDEO SARA' DISPONIBILE PREVIA BREVE REGISTRAZIONE CON INSERIMENTO DI NOME, COGNOME, NOME AZIENDA.`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          children: [
            new TextRun({
              text: `BUONA VISIONE`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
      ],
    });

    // Used to export the file into a .docx file
    Packer.toBlob(doc).then((blob) => {
      FileSaver.saveAs(blob, `Cartello-Video-Sicurezza-Fornitori_${this.company.get("name")}.docx`);
    });
  }

  /**
   * Handle Qrcode send by email
   */
  onSafetyVideoSuppliersSendQrcodeByEmail(): void {
    const qrImage = document
      .getElementById("safetyVideoSuppliersQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          qrImage,
          phonebook: this.company.get("phonebook"),
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }
  /**
   * Export safetyVideoSuppliers in a file with given format
   *
   * @since 1.0.0
   */
  onExportSafetyVideoSuppliers(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "companies.detail.exportSafetyVideoSuppliersFile"
    )} ${date}`;
    this.subscriptions.push(
      this.videoViewService.getSuppliersVideoExport(this.companyId).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  
  /**
   * Init safetyVideoResidents data table
   *
   * @since 1.0.0
   */
  initSafetyVideoResidentsDataTable(): void {
    // Load first set of data
    this.loadSafetyVideoResidentsPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.safetyVideoResidentsSort.sortChange.subscribe(
        () => (this.safetyVideoResidentsPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.safetyVideoResidentsSort.sortChange, this.safetyVideoResidentsPaginator.page)
        .pipe(tap(() => this.loadSafetyVideoResidentsPage()))
        .subscribe()
    );

    // // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.safetyVideoResidentsInput.nativeElement, "keyup")
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.safetyVideoResidentsPaginator.pageIndex = 0; // Reset page
    //         this.loadSafetyVideoResidentsPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    combineLatest([
      this.safetyVideoResidentsFirstNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoResidentsLastNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoResidentsCompanyNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoResidentsCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
    ])
      .pipe(
        debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
      )
      .subscribe((res) => {
        // Load page only if at least one value is not null
        if (!res.every((value) => value === null)) {
          this.loadSafetyVideoResidentsPage();
        }
      });
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadSafetyVideoResidentsPage(): void {
    const start = this.safetyVideoResidentsPaginator.pageIndex * this.safetyVideoResidentsPaginator.pageSize;
    const length = this.safetyVideoResidentsPaginator.pageSize;
    const search = {
      firstName: this.safetyVideoResidentsFirstNameFilter.value,
      lastName: this.safetyVideoResidentsLastNameFilter.value,
      companyName: this.safetyVideoResidentsCompanyNameFilter.value,
      createdAt: this.safetyVideoResidentsCreatedAtFilter.value,
    };
    this.safetyVideoResidentsDataSource.loadResidentsVideoItems(
      this.companyId,
      start,
      length,
      this.safetyVideoResidentsSort.active,
      this.safetyVideoResidentsSort.direction,
      search
    );
  }

  
  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFiltersSafetyVideoResidents(): void {
    // Reset lists service data
    this.listsService.setListSetting("safetyVideoResidents", 0, 10, {});

    // Reset tables' filters
    // this.safetyVideoResidentsInput.nativeElement.value = "";
    this.safetyVideoResidentsFirstNameFilter.setValue(null);
    this.safetyVideoResidentsLastNameFilter.setValue(null);
    this.safetyVideoResidentsCompanyNameFilter.setValue(null);
    this.safetyVideoResidentsCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.safetyVideoResidentsSort.direction = "asc";
    this.safetyVideoResidentsSort.active = "createdAt";
    this.safetyVideoResidentsSort._stateChanges.next();

    // Reset safetyVideoResidentsPaginator
    this.safetyVideoResidentsPaginator.pageIndex = 0;
    this.safetyVideoResidentsPaginator.pageSize = 10;

    // Reload table's data
    this.loadSafetyVideoResidentsPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilterSafetyVideoResidents(filter: FormControl): void {
    filter.reset();
    this.loadSafetyVideoResidentsPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInputSafetyVideoResidents(): void {
    this.safetyVideoResidentsInput.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    this.safetyVideoResidentsInput.nativeElement.dispatchEvent(event);
  }
  
  /**
   * Handle click open QRcode safetyVideoResidents dialogbox
   *
   * @since 1.0.0
   */
  onSafetyVideoResidentsPrintQRcode(): void {
    this.dialog.open(PrintQrcodeDialogComponent, {
      data: {
        qrCode: this.safetyVideoResidentsQrcodeData,
        name: this.company.get("name"),
        city: this.company.get("legalAddress.city"),
        address: `${this.company.get("legalAddress.route")} ${this.company.get("legalAddress.streetNumber")}`
      },
      width: "700px",
    });
  }

  /**
   * Handle generate QRcode docx
   *
   * @since 1.0.0
   */
  onSafetyVideoResidentsGenerateQrCodeDoc(): void {
    const qrImage = document
      .getElementById("safetyVideoResidentsQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Create document data
    const doc = new Document();
    const image = Media.addImage(doc, qrImage, 150, 150, {
      floating: {
        horizontalPosition: {
          relative: HorizontalPositionRelativeFrom.PAGE,
          align: HorizontalPositionAlign.CENTER,
        },
        verticalPosition: {
          relative: VerticalPositionRelativeFrom.PARAGRAPH,
          align: VerticalPositionAlign.CENTER,
        },
      },
    });

    doc.addSection({
      properties: {},
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 400 },
          children: [
            new TextRun({
              text: `AMMINISTRATORE ${this.company.get("name")}`,
              bold: true,
              allCaps: true,
              color: "auto",
              size: 24 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          children: [
            new TextRun({
              text: `ATTENZIONE`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 28 * 2,
              underline: {
                color: "FF0000",
                type: UnderlineType.SINGLE,
              },
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: `AVVISO A TUTTI I CONDOMINI`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 28 * 2,
              underline: {
                color: "FF0000",
                type: UnderlineType.SINGLE,
              },
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: `PRIMA DI ACCEDERE ALLE PARTI COMUNI DEL CONDOMINIO E' NECESSARIO VISUALIZZARE L'INFORMATIVA SULLA SICUREZZA E I DIVIETI`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 22 * 2,
            }),
          ],
        }),
        new Paragraph(image),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 2500 },
          children: [
            new TextRun({
              text: `VIDEO INFORMATIVO AMMINISTRATORE ${this.company.get("name")}`,
              font: "Times New Roman",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 12 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_2,
          children: [
            new TextRun({
              text: `${this.company.get("legalAddress.route")}, ${this.company.get(
                "legalAddress.streetNumber"
              )}`,
              font: "Times New Roman",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 12 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 2000, after: 200 },
          children: [
            new TextRun({
              text: `SI PREGA DI INQUADRARE IL QR-CODE DIRETTAMENTE CON LA FOTOCAMERA DEL VOSTRO DISPOSITIVO (SE ABILITATO) O ATTRAVERSO UN'APPLICAZIONE DEDICATA.`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: `DOPO L'INQUADRATURA IL VIDEO SARA' DISPONIBILE PREVIA BREVE REGISTRAZIONE CON INSERIMENTO DI NOME, COGNOME, NOME AZIENDA.`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          children: [
            new TextRun({
              text: `BUONA VISIONE`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
      ],
    });

    // Used to export the file into a .docx file
    Packer.toBlob(doc).then((blob) => {
      FileSaver.saveAs(blob, `Cartello-Video-Sicurezza-Fornitori_${this.company.get("name")}.docx`);
    });
  }

  /**
   * Handle Qrcode send by email
   */
  onSafetyVideoResidentsSendQrcodeByEmail(): void {
    const qrImage = document
      .getElementById("safetyVideoResidentsQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          qrImage,
          phonebook: this.company.get("phonebook"),
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  /**
   * Export safetyVideoResidents in a file with given format
   *
   * @since 1.0.0
   */
  onExportSafetyVideoResidents(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "companies.detail.exportSafetyVideoResidentsFile"
    )} ${date}`;
    this.subscriptions.push(
      this.videoViewService.getResidentsVideoExport(this.companyId).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }



  // ### START - Study archive ###

  /**
   * Init genericDocs data table
   *
   * @since 1.0.0
   */
  initGenericDocsDataTable(): void {
    // Load first set of data
    this.genericDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.genericDocsSort.sortChange.subscribe(() => this.genericDocsPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.genericDocsSort.sortChange, this.genericDocsPaginator.page)
        .pipe(
          tap(() => this.genericDocsLoadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.genericDocsNameFilter.valueChanges.pipe(startWith(null as string)),
        this.genericDocsDescriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.genericDocsCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.genericDocsLoadPage();
          }
        }
        )
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  genericDocsLoadPage(): void {
    const start = this.genericDocsPaginator.pageIndex * this.genericDocsPaginator.pageSize;
    const length = this.genericDocsPaginator.pageSize;
    const search = {
      name: this.genericDocsNameFilter.value,
      description: this.genericDocsDescriptionFilter.value,
      createdAt: this.genericDocsCreatedAtFilter.value,
    };
    this.genericDocsDataSource.loadDocumentsItems(this.companyId, start, length, this.genericDocsSort.active, this.genericDocsSort.direction, search);
  }

  /**
   * Export generic docs in a file with given format
   *
   * @since 1.0.0
   */
  onGenericDocsExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('companies.detail.exportGenericDocsFile')} ${date}`;
    this.subscriptions.push(
      this.genericDocsService.getExport(this.companyId).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onGenericDocsResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('genericDocs', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.genericDocsNameFilter.setValue(null);
    this.genericDocsDescriptionFilter.setValue(null);
    this.genericDocsCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.genericDocsSort.direction = "desc";
    this.genericDocsSort.active = "createdAt";
    this.genericDocsSort._stateChanges.next();

    // Reset paginator
    this.genericDocsPaginator.pageIndex = 0;
    this.genericDocsPaginator.pageSize = 10;

    // Reload table's data
    this.genericDocsLoadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onGenericDocsResetFilter(filter: FormControl): void {
    filter.reset();
    this.genericDocsLoadPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  genericDocsResetInput(): void {
    // this.genericDocsInput.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.genericDocsInput.nativeElement.dispatchEvent(event);
  }

  /**
   * Handler Add new doc
   */
  onGenericDocsAddDoc(): void {
    const dialogRef = this.dialog.open(GenericDocsAddDocDialogComponent, {
      data: {
        companyId: this.companyId
      },
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.genericDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Edit doc
   */
  onGenericDocsEdit(data: any): void {
    const dialogRef = this.dialog.open(GenericDocsEditDocDialogComponent, {
      data,
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.genericDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Delete doc
   */
  onGenericDocsDelete(docId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('genericDocs.deleteDoc.title'),
        message: this.translateService.instant('genericDocs.deleteDoc.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            this.genericDocsService.delete(this.companyId, docId)
              .subscribe(
                res => {
                  if (res.status) {
                    this.genericDocsLoadPage();
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          }
        }
      );
  }

  /**
   * Handler View standard QRcode doc
   */
  onGenericDocsViewQrcode(data: any): void {
    this.dialog.open(GenericDocsQrCodeDialogComponent, {
      data: {
        data,
        print: false,
      },
      width: "450px",
    });
  }

  /**
   * Handler Print standard QRcode doc
   */
  onGenericDocsPrintQrcode(data: any): void {
    this.dialog.open(GenericDocsQrCodeDialogComponent, {
      data: {
        data,
        print: true,
      },
      width: "450px",
    });
  }

  /**
   * Handler download files
   */
  onGenericDocsDownload(data: any): void {
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        documents: data.files,
      },
      width: "600px",
    });
  }

  // ### END - Study archive ###

    
  // ### START - Safety Fire ###

  /**
   * Init fireSafetyDocs data table
   *
   * @since 1.0.0
   */
  initFireSafetyDocsDataTable(): void {
    // Load first set of data
    this.fireSafetyDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.fireSafetyDocsSort.sortChange.subscribe(() => this.fireSafetyDocsPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.fireSafetyDocsSort.sortChange, this.fireSafetyDocsPaginator.page)
        .pipe(
          tap(() => this.fireSafetyDocsLoadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.fireSafetyDocsNameFilter.valueChanges.pipe(startWith(null as string)),
        this.fireSafetyDocsDescriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.fireSafetyDocsCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.fireSafetyDocsLoadPage();
          }
        }
        )
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  fireSafetyDocsLoadPage(): void {
    const start = this.fireSafetyDocsPaginator.pageIndex * this.fireSafetyDocsPaginator.pageSize;
    const length = this.fireSafetyDocsPaginator.pageSize;
    const search = {
      name: this.fireSafetyDocsNameFilter.value,
      description: this.fireSafetyDocsDescriptionFilter.value,
      createdAt: this.fireSafetyDocsCreatedAtFilter.value,
    };
    this.fireSafetyDocsDataSource.loadDocumentsItems(this.companyId, start, length, this.fireSafetyDocsSort.active, this.fireSafetyDocsSort.direction, search);
  }

  /**
   * Export generic docs in a file with given format
   *
   * @since 1.0.0
   */
  onFireSafetyDocsExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('companies.detail.exportFireSafetyDocsFile')} ${date}`;
    this.subscriptions.push(
      this.fireSafetyDocsService.getExport(this.companyId).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onFireSafetyDocsResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('fireSafetyDocs', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.fireSafetyDocsNameFilter.setValue(null);
    this.fireSafetyDocsDescriptionFilter.setValue(null);
    this.fireSafetyDocsCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.fireSafetyDocsSort.direction = "desc";
    this.fireSafetyDocsSort.active = "createdAt";
    this.fireSafetyDocsSort._stateChanges.next();

    // Reset paginator
    this.fireSafetyDocsPaginator.pageIndex = 0;
    this.fireSafetyDocsPaginator.pageSize = 10;

    // Reload table's data
    this.fireSafetyDocsLoadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onFireSafetyDocsResetFilter(filter: FormControl): void {
    filter.reset();
    this.fireSafetyDocsLoadPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  fireSafetyDocsResetInput(): void {
    // this.fireSafetyDocsInput.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.fireSafetyDocsInput.nativeElement.dispatchEvent(event);
  }

  /**
   * Handler Add new doc
   */
  onFireSafetyDocsAddDoc(): void {
    const dialogRef = this.dialog.open(FireSafetyDocsAddDocDialogComponent, {
      data: {
        companyId: this.companyId
      },
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.fireSafetyDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Edit doc
   */
  onFireSafetyDocsEdit(data: any): void {
    const dialogRef = this.dialog.open(FireSafetyDocsEditDocDialogComponent, {
      data,
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.fireSafetyDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Delete doc
   */
  onFireSafetyDocsDelete(docId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('fireSafetyDocs.deleteDoc.title'),
        message: this.translateService.instant('fireSafetyDocs.deleteDoc.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            this.fireSafetyDocsService.delete(this.company.get("_id"), docId)
              .subscribe(
                res => {
                  if (res.status) {
                    this.fireSafetyDocsLoadPage();
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          }
        }
      );
  }

  /**
   * Handler View standard QRcode doc
   */
  onFireSafetyDocsViewQrcode(data: any): void {
    this.dialog.open(FireSafetyDocsQrCodeDialogComponent, {
      data: {
        data,
        print: false,
      },
      width: "450px",
    });
  }

  /**
   * Handler Print standard QRcode doc
   */
  onFireSafetyDocsPrintQrcode(data: any): void {
    this.dialog.open(FireSafetyDocsQrCodeDialogComponent, {
      data: {
        data,
        print: true,
      },
      width: "450px",
    });
  }

  /**
   * Handler download files
   */
  onFireSafetyDocsDownload(data: any): void {
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        documents: data.files,
      },
      width: "600px",
    });
  }

  // ### END - Fire Safety ###
  
  /**
   * open pop up Video
   *
   * @since 1.0.0
   */
  onVideoPopUp(videoType: string): void {
    let title = '';
    let videoName = '';
    switch(videoType) {
      case 'SafetyVideoSuppliers': 
        title = this.translateService.instant("companies.detail.safetyVideoSuppliersCard.title");
        videoName = 'Archedi-SD3';
      break;
      case 'SafetyVideoResidents': 
        title = this.translateService.instant("companies.detail.safetyVideoResidentsCard.title");
        videoName = 'Archedi-Resident';
      break;
    }
    const width = "800px";
    this.dialog.open(VideoDialogComponent, {
      data: { 
        title,
        videoName
      },
      width,
      maxWidth: width,
    });
  }

  /** 
   *  Export contract in docx
   * 
   * @since 1.0.0 
  */
  onGetContract(): void {
    this.detailCardIsLoading = true;
    PizZipUtils.getBinaryContent(
      'https://api.archedi.it/uploads/CONTRATTO-UTILIZZO-PIATTAFORMA-ARCHEDI.docx',
      (error: Error | null, content: string) => {
        try {
          if (error) {
            throw error;
          }
          const zip = new PizZip(content);
          const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
          });
          
          doc.setData({
            company: this.companyData.name,
            legalAddress: `${this.companyData.legalAddress.route} ${this.companyData.legalAddress.streetNumber} - ${this.companyData.legalAddress.postalCode} ${this.companyData.legalAddress.city} ${this.companyData.legalAddress.countryCodeTwo}`,
            operativeAddres: this.companyData.operativeAddress ? `${this.companyData.operativeAddress.route} ${this.companyData.operativeAddress.streetNumber} - ${this.companyData.operativeAddress.postalCode} ${this.companyData.operativeAddress.city} ${this.companyData.operativeAddress.countryCodeTwo}` : `${this.companyData.legalAddress.route} ${this.companyData.legalAddress.streetNumber} - ${this.companyData.legalAddress.postalCode} ${this.companyData.legalAddress.city} ${this.companyData.legalAddress.countryCodeTwo}`,
            taxCode: this.companyData.taxCode ? this.companyData.taxCode : '……………………………',
            vatNumber: this.companyData.vatNumber ? this.companyData.vatNumber : '……………………………',
            date: moment(this.disclaimerAt).isValid() ? moment(this.disclaimerAt).format('DD-MM-YYYY hh:mm:ss'):'',
            fullName: this.disclaimerUser,
          });

          // render the document => replace all occurences
          doc.render();
          
          const out = doc.getZip().generate({
            type: 'blob',
            mimeType:
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
          // Output the document using Data-URI
          FileSaver.saveAs(out, 'CONTRATTO-UTILIZZO-PIATTAFORMA-ARCHEDI.docx');
          this.detailCardIsLoading = false;
        } catch (error) {
          this.detailCardIsLoading = false;
          this.helperService.handleError(JSON.stringify(error));
        }
      }
    );
  }

  // ### START - Privacy ###

  /**
   * Init privacyDocs data table
   *
   * @since 1.0.0
   */
  initPrivacyDocsDataTable(): void {
    if (this.hiddenCardPrivacy) {
      // Privacy / Privacy SAG not active for this company: skip the API call entirely
      return;
    }

    // Load first set of data
    this.privacyDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.privacyDocsSort.sortChange.subscribe(() => this.privacyDocsPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.privacyDocsSort.sortChange, this.privacyDocsPaginator.page)
        .pipe(
          tap(() => this.privacyDocsLoadPage())
        )
        .subscribe()
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  privacyDocsLoadPage(): void {
    const start = this.privacyDocsPaginator.pageIndex * this.privacyDocsPaginator.pageSize;
    const length = this.privacyDocsPaginator.pageSize;
    // Companies with "privacySag" see the property and supplier privacy docs; the back-end only
    // supports filtering by a single type value, so both are requested together and
    // filtered/paginated client-side (@see PrivacyDocsDataSource). "privacy" + "suppliers"
    // (but not "privacySag") only sees the supplier doc. "Nota informativa" has its own card.
    let search: { type: string } | undefined;
    let allowedTypes: string[] | undefined;
    if (this.privacySagActive) {
      allowedTypes = [PROPERTY_PRIVACY_TEMPLATE, SUPPLIER_PRIVACY_TEMPLATE];
    } else if (this.suppliers) {
      search = { type: SUPPLIER_PRIVACY_TEMPLATE };
    }
    this.privacyDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.privacyDocsSort.active, this.privacyDocsSort.direction, search, allowedTypes);
  }

  /**
   * Init the "Nota informativa" docs data table (condo-owner privacy notice only)
   *
   * @since 3.0.0
   */
  initInformativeNoteDocsDataTable(): void {
    if (!this.informativeNoteActive || !this.informativeNoteDocsSort || !this.informativeNoteDocsPaginator) {
      // "Nota informativa" not active (or its card is not rendered): skip the API call entirely
      return;
    }

    // Load first set of data
    this.informativeNoteDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.informativeNoteDocsSort.sortChange.subscribe(() => this.informativeNoteDocsPaginator.pageIndex = 0)
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.informativeNoteDocsSort.sortChange, this.informativeNoteDocsPaginator.page)
        .pipe(
          tap(() => this.informativeNoteDocsLoadPage())
        )
        .subscribe()
    );
  }

  /**
   * Load requested "Nota informativa" docs data page
   *
   * @since 3.0.0
   */
  informativeNoteDocsLoadPage(): void {
    const start = this.informativeNoteDocsPaginator.pageIndex * this.informativeNoteDocsPaginator.pageSize;
    const length = this.informativeNoteDocsPaginator.pageSize;
    this.informativeNoteDocsDataSource.loadDocumentsItems(
      this.company.get('_id'),
      start,
      length,
      this.informativeNoteDocsSort.active,
      this.informativeNoteDocsSort.direction,
      { type: PROPERTY_PRIVACY_TEMPLATE }
    );
  }

  /**
   * Check that a privacy doc link is a safe, absolute http(s) URL
   * before using it to navigate or render it inside the UI
   *
   * @since 1.0.0
   */
  isValidPrivacyDocLink(link: string | null): boolean {
    if (!link) {
      return false;
    }
    try {
      const url = new URL(link);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (err) {
      return false;
    }
  }

  /**
   * Build the public, unauthenticated URL for this privacy doc's general
   * access page (@see PrivacyDocsPublicAccessComponent): the visitor lands
   * there, enters a property's tax code, and downloads the document once
   * it's confirmed to belong to this company.
   *
   * @since 1.0.0
   */
  buildPrivacyDocPublicLink(docType: string): string | null {
    const companyId = this.company.get('_id');
    const segmentByType: { [key: string]: string } = {
      "informativa_privacy_immobile": 'condo-owner',
      "informativa_privacy_fornitore": 'supplier',
    };
    const segment = segmentByType[docType];
    if (!companyId || !segment) {
      return null;
    }
    return `${window.location.origin}/privacy-docs/${companyId}/${segment}`;
  }

  /**
   * Handle "Accedi" action: open the privacy doc's public access page in a new tab
   *
   * @since 1.0.0
   */
  onPrivacyDocsAccess(element: any): void {
    const link = this.buildPrivacyDocPublicLink(element && element.type);
    if (!link || !this.isValidPrivacyDocLink(link)) {
      this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  /**
   * Handle "Visualizza QR-code" action: open a dialog showing the QR-code
   * that encodes the privacy doc's public access page link
   *
   * @since 1.0.0
   */
  onPrivacyDocsViewQrcode(element: any): void {
    const link = this.buildPrivacyDocPublicLink(element && element.type);
    if (!link || !this.isValidPrivacyDocLink(link)) {
      this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
      return;
    }
    this.dialog.open(PrivacyDocsQrCodeDialogComponent, {
      minWidth: '40%',
      data: {
        data: { ...element, link },
      },
    });
  }

  /**
   * Handle "Visualizza link" action: open a dialog showing the privacy doc's
   * public access page link
   *
   * @since 1.0.0
   */
  onPrivacyDocsViewLink(element: any): void {
    const link = this.buildPrivacyDocPublicLink(element && element.type);
    if (!link || !this.isValidPrivacyDocLink(link)) {
      this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
      return;
    }
    this.dialog.open(PrivacyDocsLinkDialogComponent, {
      minWidth: '40%',
      data: {
        data: { ...element, link },
      },
    });
  }


  // ### END - Privacy ###

  // ### START - Privacy Studio Docs ###

  /**
   * Handle "Genera Nota informativa" action: download the generated PDF
   * (4 QR codes: residents and suppliers safety videos, GSA manual, condo-owner privacy access key)
   *
   * @since 3.0.0
   */
  onInformativeNoteDownload(): void {
    if (!this.informativeNoteActive || this.informativeNoteDocIsLoading) {
      return;
    }
    this.informativeNoteDocIsLoading = true;
    this.subscriptions.push(
      this.informativeNoteService.generateDocumentFile(this.company.get('_id'))
        .pipe(finalize(() => this.informativeNoteDocIsLoading = false))
        .subscribe(
          res => FileSaver.saveAs(res.blob, res.fileName),
          err => this.helperService.handleError(err)
        )
    );
  }

  /**
   * Handle "Invia Nota informativa" action: generate the document, upload it and
   * open the email dialog with the document already attached
   *
   * @since 3.0.0
   */
  onInformativeNoteSendByEmail(): void {
    if (!this.informativeNoteActive || this.informativeNoteDocIsLoading) {
      return;
    }
    this.informativeNoteDocIsLoading = true;
    this.subscriptions.push(
      this.informativeNoteService.generateDocumentFile(this.company.get('_id'))
        .pipe(
          concatMap(res => this.informativeNoteService.uploadForEmail(res.blob, res.fileName)),
          finalize(() => this.informativeNoteDocIsLoading = false)
        )
        .subscribe(
          (files: any[]) => {
            const dialogRef = this.newMessageDialog.open(SendEmailDialogComponent, {
              minWidth: "60%",
              maxHeight: "98vh",
              data: {
                phonebook: this.company.get("phonebook") ? this.company.get("phonebook") : [],
                insertedDocs: [],
                selectedDocs: [],
                attachments: files,
                hideInsertedDocs: true,
                companyData: this.loggedUserMaster ? this.companyData : "",
                propertyName: "",
                propertyId: "",
                sentFromSupplierPage: false,
              },
              disableClose: true,
            });
            dialogRef.beforeClosed()
              .pipe(take(1))
              .subscribe(res => {
                if (res) {
                  this.toastrService.success(this.translateService.instant("communications.add.sendedSuccessfully"));
                }
              });
          },
          err => this.helperService.handleError(err)
        )
    );
  }

  /**
   * Handle "Scarica template" action: open a dialog listing the
   * company-level ("studio") privacy document templates available for
   * download, pre-filled with this company's own registry data
   * (Privacy SAG subscription required, gated in the template via
   * privacySagActive)
   *
   * @since 1.0.0
   */
  onOpenStudioPrivacyTemplatesDialog(): void {
    if (!this.privacySagActive) {
      return;
    }

    this.dialog.open(StudioPrivacyTemplatesDialogComponent, {
      data: {
        companyId: this.company.get("_id"),
      },
      width: "650px",
      disableClose: true,
    });
  }

  /**
   * Init privacyStudioDocs data table
   *
   * @since 1.0.0
   */
  initPrivacyStudioDocsDataTable(): void {
    if (this.hiddenCardPrivacy) {
      // Privacy / Privacy SAG not active for this company: skip the API call entirely
      return;
    }

    // Load first set of data
    this.privacyStudioDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.privacyStudioDocsSort.sortChange.subscribe(() => this.privacyStudioDocsPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.privacyStudioDocsSort.sortChange, this.privacyStudioDocsPaginator.page)
        .pipe(
          tap(() => this.privacyStudioDocsLoadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.privacyStudioDocsTypeFilter.valueChanges.pipe(startWith(null as string)),
        this.privacyStudioDocsDescriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.privacyStudioDocsCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.privacyStudioDocsLoadPage();
          }
        }
        )
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  privacyStudioDocsLoadPage(): void {
    const start = this.privacyStudioDocsPaginator.pageIndex * this.privacyStudioDocsPaginator.pageSize;
    const length = this.privacyStudioDocsPaginator.pageSize;
    const search = {
      type: this.privacyStudioDocsTypeFilter.value,
      description: this.privacyStudioDocsDescriptionFilter.value,
      createdAt: this.privacyStudioDocsCreatedAtFilter.value,
    };
    this.privacyStudioDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.privacyStudioDocsSort.active, this.privacyStudioDocsSort.direction, search);
  }

  /**
   * Export privacy studio docs in a file with given format
   *
   * @since 1.0.0
   */
  onPrivacyStudioDocsExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('companies.detail.exportPrivacyStudioDocsFile')} ${date}`;
    this.subscriptions.push(
      this.privacyStudioDocsService.getExport(this.company.get('_id')).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onPrivacyStudioDocsResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('privacyStudioDocs', 0, 10, {});

    // Reset tables' filters
    this.privacyStudioDocsTypeFilter.setValue(null);
    this.privacyStudioDocsDescriptionFilter.setValue(null);
    this.privacyStudioDocsCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.privacyStudioDocsSort.direction = "desc";
    this.privacyStudioDocsSort.active = "createdAt";
    this.privacyStudioDocsSort._stateChanges.next();

    // Reset paginator
    this.privacyStudioDocsPaginator.pageIndex = 0;
    this.privacyStudioDocsPaginator.pageSize = 10;

    // Reload table's data
    this.privacyStudioDocsLoadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onPrivacyStudioDocsResetFilter(filter: FormControl): void {
    filter.reset();
    this.privacyStudioDocsLoadPage();
  }

  /**
   * Handler Add new doc
   */
  onPrivacyStudioDocsAddDoc(): void {
    const dialogRef = this.dialog.open(PrivacyStudioDocsAddDocDialogComponent, {
      data: {
        companyId: this.company.get("_id")
      },
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.privacyStudioDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Edit doc
   */
  onPrivacyStudioDocsEdit(data: any): void {
    const dialogRef = this.dialog.open(PrivacyStudioDocsEditDocDialogComponent, {
      data,
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.privacyStudioDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Delete doc
   */
  onPrivacyStudioDocsDelete(docId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('companies.detail.privacyDocsCard.studioSection.dialog.deleteDoc.title'),
        message: this.translateService.instant('companies.detail.privacyDocsCard.studioSection.dialog.deleteDoc.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            this.privacyStudioDocsService.delete(this.company.get("_id"), docId)
              .subscribe(
                res => {
                  if (res.status) {
                    this.privacyStudioDocsLoadPage();
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          }
        }
      );
  }

  /**
   * Handler View standard QRcode doc
   */
  onPrivacyStudioDocsViewQrcode(data: any): void {
    this.dialog.open(PrivacyStudioDocsQrCodeDialogComponent, {
      data: {
        data,
        print: false,
      },
      width: "450px",
    });
  }

  /**
   * Handler Print standard QRcode doc
   */
  onPrivacyStudioDocsPrintQrcode(data: any): void {
    this.dialog.open(PrivacyStudioDocsQrCodeDialogComponent, {
      data: {
        data,
        print: true,
      },
      width: "450px",
    });
  }

  /**
   * Handler download files
   */
  onPrivacyStudioDocsDownload(data: any): void {
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        documents: data.files,
      },
      width: "600px",
    });
  }

  /**
   * Update docs list to send by email
   *
   * @since 1.0.0
   */
  onPrivacyStudioDocSelected(doc: any, checked: boolean): void {
    if (doc && checked) {
      this.selectedPrivacyStudioDocs.push(doc);
    } else if (doc && !checked) {
      const index = this.selectedPrivacyStudioDocs.indexOf(doc._id);
      this.selectedPrivacyStudioDocs.splice(index, 1);
    }
  }

  /**
   * Handle send selected docs by email
   */
  onPrivacyStudioDocsSendByEmail(): void {
    const insertedDocs: any[] = [];
    const selectedDocs: any[] = [];
    this.selectedPrivacyStudioDocs.forEach((doc: any) => {
      selectedDocs.push(doc._id);
      const data = {
        document: doc.files,
      };
      insertedDocs.push({
        docId: doc._id,
        title: doc.type,
        data,
      });
    });

    const newMessageDialogRef = this.newMessageDialog.open(
      SendEmailDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          phonebook: this.company.get("phonebook")
            ? this.company.get("phonebook")
            : [],
          insertedDocs,
          selectedDocs,
          companyData: this.loggedUserMaster ? this.companyData : "",
          propertyName: "",
          propertyId: "",
          sentFromSupplierPage: false,
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  // ### END - Privacy Studio Docs ###
}
